import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { saasGetAppWebhook, saasSaveAppWebhook, saasGetAppStoreKit, saasSaveAppStoreKit, saasGetAppGooglePlay, saasSaveAppGooglePlay, saasDeleteApp } from '../../lib/portalApi';
import { Modal } from '../ui/Modal';

/** Đọc file người dùng chọn thành chuỗi — `.p8` và service-account JSON đều là văn bản. */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = () => reject(new Error('Không đọc được file'));
    r.readAsText(file);
  });
}

export function SettingsView({ appId, tenantId, packageName }: { appId: string, tenantId: string, packageName?: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookSecretMasked, setWebhookSecretMasked] = useState('');
  
  const [bundleId, setBundleId] = useState('');
  const [issuerId, setIssuerId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [p8Content, setP8Content] = useState('');
  const [hasP8Content, setHasP8Content] = useState(false);

  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [hasServiceAccountJson, setHasServiceAccountJson] = useState(false);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (!user) return;
    const fetchSettings = async () => {
      try {
        const idToken = await user.getIdToken();
        const [webhook, storekit, googlePlay] = await Promise.all([
          saasGetAppWebhook({ tenantId, appId, idToken }),
          saasGetAppStoreKit({ tenantId, appId, idToken }),
          saasGetAppGooglePlay({ tenantId, appId, idToken }),
        ]);
        setWebhookUrl(webhook.url || '');
        setWebhookSecret('');
        setWebhookSecretMasked(webhook.secretMasked || '');
        
        setBundleId(storekit.bundleId || '');
        setIssuerId(storekit.issuerId || '');
        setKeyId(storekit.keyId || '');
        setP8Content('');
        setHasP8Content(!!storekit.hasP8Content);
        
        setServiceAccountJson('');
        setHasServiceAccountJson(!!googlePlay.hasServiceAccountJson);
      } catch (err: any) {
        console.error('Failed to fetch settings', err);
      }
    };
    fetchSettings();
  }, [user, appId, tenantId]);

  const handleSaveWebhook = async () => {
    if (!user) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const idToken = await user.getIdToken();
      await saasSaveAppWebhook({ tenantId, appId, url: webhookUrl, secret: webhookSecret, idToken });
      setMessage('Webhook configuration saved successfully!');
      if (webhookSecret) setWebhookSecretMasked('••••' + webhookSecret.slice(-4));
      setWebhookSecret('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save webhook');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveStoreKit = async () => {
    if (!user) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const idToken = await user.getIdToken();
      await saasSaveAppStoreKit({ tenantId, appId, bundleId, issuerId, keyId, p8Content, idToken });
      setMessage('StoreKit configuration saved successfully!');
      if (p8Content) setHasP8Content(true);
      setP8Content('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save StoreKit');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveGooglePlay = async () => {
    if (!user) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const idToken = await user.getIdToken();
      await saasSaveAppGooglePlay({ tenantId, appId, serviceAccountJson, idToken });
      setMessage('Google Play configuration saved successfully!');
      if (serviceAccountJson) setHasServiceAccountJson(true);
      setServiceAccountJson('');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save Google Play');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteApp = async () => {
    if (!user) return;
    if (deleteConfirmText !== appId) {
      setError('Confirmation text does not match the App ID.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const idToken = await user.getIdToken();
      await saasDeleteApp({ appId, idToken });
      navigate('/apps');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete app');
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-lg max-w-5xl">
      
      {message && (
        <div className="p-md bg-[#15803d]/10 border border-[#15803d]/20 text-[#15803d] rounded-lg font-body-sm shadow-sm">
          {message}
        </div>
      )}

      {error && (
        <div className="p-md bg-error-container text-on-error-container border border-error/20 rounded-lg font-body-sm shadow-sm">
          {error}
        </div>
      )}

      {/* Webhook Configuration */}
      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
        <h2 className="font-headline-md text-[20px] font-semibold text-on-surface mb-2">Webhook Configuration</h2>
        <p className="font-body-sm text-[13px] text-on-surface-variant mb-lg">
          MTSubKit will send an HTTP POST request to this URL whenever there is a payment or renewal event from your users.
        </p>

        <div className="flex flex-col gap-md max-w-2xl">
          <div>
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Webhook URL</label>
            <input 
              className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
              placeholder="https://api.yourdomain.com/webhook"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Webhook Secret (Optional)</label>
            <input 
              className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface placeholder:text-on-surface-variant" 
              placeholder={webhookSecretMasked ? `(Masked: ${webhookSecretMasked}) - Leave empty to keep` : "Secret string to authenticate the request"}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mt-2">
            <button 
              onClick={handleSaveWebhook} 
              disabled={busy}
              className="bg-surface-container border border-outline-variant text-on-surface font-label-md px-lg py-sm rounded-lg hover:bg-surface-container-high transition-colors shadow-sm disabled:opacity-50"
            >
              Save Webhook
            </button>
          </div>
        </div>
      </div>

      {/* Apple StoreKit 2 Configuration */}
      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
        <h2 className="font-headline-md text-[20px] font-semibold text-on-surface mb-2">Apple StoreKit 2 Configuration</h2>
        <p className="font-body-sm text-[13px] text-on-surface-variant mb-lg">
          Configure the Private Key (.p8) so the system can communicate with the Apple App Store Server API.
        </p>

        <div className="flex flex-col gap-md max-w-2xl">
          <div>
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Bundle ID</label>
            <input 
              className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
              placeholder="e.g. com.yourcompany.app"
              value={bundleId}
              onChange={(e) => setBundleId(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Issuer ID</label>
            <input 
              className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
              placeholder="From App Store Connect -> Keys"
              value={issuerId}
              onChange={(e) => setIssuerId(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Key ID</label>
            <input 
              className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-primary/50 text-on-surface" 
              placeholder="From App Store Connect -> Keys"
              value={keyId}
              onChange={(e) => setKeyId(e.target.value)}
              disabled={busy}
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block font-label-sm text-[12px] text-on-surface-variant">
                Private Key Content (.p8)
              </label>
                <label className="font-label-sm text-[12px] text-primary cursor-pointer hover:underline">
                  Chọn file .p8
                  <input
                    type="file"
                    accept=".p8,.pem,text/plain"
                    className="hidden"
                    disabled={busy}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try { setP8Content(await readFileAsText(f)); setError(''); }
                      catch (err: any) { setError(err.message); }
                      e.target.value = '';
                    }}
                  />
                </label>
            </div>
            <textarea
              className="w-full min-h-[150px] p-4 bg-[#1e1e1e] border border-outline-variant rounded-lg text-[#d4d4d4] font-mono text-[13px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder={hasP8Content ? "(Key is securely stored in Secret Manager. Leave empty to keep the existing key)" : "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"}
              value={p8Content}
              onChange={(e) => setP8Content(e.target.value)}
              disabled={busy}
              spellCheck={false}
            />
          </div>

          <div className="mt-2">
            <button 
              onClick={handleSaveStoreKit} 
              disabled={busy}
              className="bg-surface-container border border-outline-variant text-on-surface font-label-md px-lg py-sm rounded-lg hover:bg-surface-container-high transition-colors shadow-sm disabled:opacity-50"
            >
              Save Apple Configuration
            </button>
          </div>
        </div>
      </div>

      {/* Google Play Developer API Configuration */}
      <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-lg shadow-sm">
        <h2 className="font-headline-md text-[20px] font-semibold text-on-surface mb-2">Google Play Configuration</h2>
        <p className="font-body-sm text-[13px] text-on-surface-variant mb-lg">
          Configure the Service Account JSON so the system can verify Android purchases. If left empty, it will fallback to the global account settings.
        </p>

        <div className="flex flex-col gap-md max-w-2xl">
          <div>
            <label className="block font-label-sm text-[12px] text-on-surface-variant mb-1">Package Name</label>
            <div className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-mono text-[13px] text-on-surface">
              {packageName || <span className="text-on-surface-variant">chưa đặt</span>}
            </div>
            <p className="font-body-sm text-[11px] text-on-surface-variant mt-1">
              Sửa ở tab Remote Config — đây là thông tin nhận dạng app, không phải chứng chỉ.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block font-label-sm text-[12px] text-on-surface-variant">
                Service Account JSON
              </label>
                <label className="font-label-sm text-[12px] text-primary cursor-pointer hover:underline">
                  Chọn file .json
                  <input
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    disabled={busy}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      try { setServiceAccountJson(await readFileAsText(f)); setError(''); }
                      catch (err: any) { setError(err.message); }
                      e.target.value = '';
                    }}
                  />
                </label>
            </div>
            <textarea
              className="w-full min-h-[200px] p-4 bg-[#1e1e1e] border border-outline-variant rounded-lg text-[#d4d4d4] font-mono text-[13px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder={hasServiceAccountJson ? "(Service Account JSON is securely stored. Leave empty to keep the existing credentials)" : "{\n  \"type\": \"service_account\",\n  \"project_id\": \"...\",\n... \n}"}
              value={serviceAccountJson}
              onChange={(e) => setServiceAccountJson(e.target.value)}
              disabled={busy}
              spellCheck={false}
            />
          </div>

          <div className="mt-2">
            <button 
              onClick={handleSaveGooglePlay} 
              disabled={busy}
              className="bg-surface-container border border-outline-variant text-on-surface font-label-md px-lg py-sm rounded-lg hover:bg-surface-container-high transition-colors shadow-sm disabled:opacity-50"
            >
              Save Google Configuration
            </button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-error-container/10 border border-error/20 rounded-xl p-lg shadow-sm">
        <h2 className="font-headline-md text-[20px] font-semibold text-error mb-2">Danger Zone</h2>
        <p className="font-body-sm text-[13px] text-on-surface-variant mb-lg">
          Once you delete an app, there is no going back. Please be certain.
        </p>

        <div className="flex flex-col gap-md max-w-2xl">
          <div className="mt-2">
            <button 
              onClick={() => setShowDeleteModal(true)} 
              disabled={busy}
              className="bg-error text-on-error font-label-md px-lg py-sm rounded-lg hover:bg-error/90 transition-colors shadow-sm disabled:opacity-50"
            >
              Delete App
            </button>
          </div>
        </div>
      </div>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Application">
        <div className="flex flex-col gap-md">
          <p className="font-body-sm text-[14px] text-on-surface-variant">
            This action cannot be undone. This will permanently delete the app <strong>{appId}</strong> and remove all associated API keys and configurations.
          </p>
          <p className="font-body-sm text-[14px] text-on-surface-variant">
            Please type <strong>{appId}</strong> to confirm.
          </p>
          <input
            className="w-full bg-surface-container-low border border-outline-variant rounded-lg px-3 py-2 font-body-sm focus:outline-none focus:ring-2 focus:ring-error text-on-surface"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={busy}
          />
          {error && <p className="text-error text-sm mt-2">{error}</p>}
          <div className="flex justify-end gap-sm mt-md">
            <button 
              onClick={() => setShowDeleteModal(false)} 
              disabled={busy}
              className="px-4 py-2 text-on-surface-variant hover:bg-surface-container-low rounded-lg transition-colors font-label-md"
            >
              Cancel
            </button>
            <button 
              onClick={handleDeleteApp} 
              disabled={busy || deleteConfirmText !== appId}
              className="bg-error text-on-error px-4 py-2 rounded-lg transition-all hover:bg-error/90 disabled:opacity-50 font-label-md"
            >
              I understand the consequences, delete this app
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
